import { IntlMessageFormat } from 'intl-messageformat';

// Locale-grouped numbers in every message ("10,000" en, "10.000" de) — the
// web side of Flutter's `"format": "decimalPattern"` on the ARB's numeric
// placeholders. intl-messageformat renders a bare `{count}` with
// String(value) ("10000"); only `#` inside a plural and `{count, number}` go
// through Intl.NumberFormat. So before formatting, every bare argument whose
// value is a number is turned into a number argument in the parsed message.
// The plural/select selectors keep the raw number, so plural choice is
// unchanged.
//
// Exceptions mirror the ARB: numeric placeholders declared WITHOUT a format
// stay as written (an id is "#110000", never "#110,000"). numberArgs.test.ts
// checks this table against lib/l10n/app_en.arb, so it can't drift.

/** Numeric placeholders the ARB declares without `decimalPattern`. */
export const RAW_NUMBER_ARGS: Readonly<Record<string, readonly string[]>> = {
  adminWordEditorSenseHeader: ['id'],
  a11yAvatarColor: ['index'],
};

/** Placeholders the ARB formats with a fixed number of decimals
 *  (`decimalPatternDigits`). */
export const DECIMAL_DIGITS_ARGS: Readonly<Record<string, Readonly<Record<string, number>>>> = {
  importAudioStorage: { used: 1 },
};

type Ast = ReturnType<IntlMessageFormat['getAst']>;
type El = Ast[number];

// TYPE from @formatjs/icu-messageformat-parser (a dependency of
// intl-messageformat, not of this app — so the values, not the import).
const ARGUMENT = 1;
const NUMBER = 2;
const SELECT = 5;
const PLURAL = 6;
const TAG = 8;

/** The AST with every bare argument named in [names] turned into a plain
 *  number argument (default Intl.NumberFormat = locale grouping). */
export function groupArgs(ast: Ast, names: ReadonlySet<string>): Ast {
  const walk = (els: Ast): Ast =>
    els.map((el): El => {
      const e = el as El & { type: number; value?: unknown };
      if (e.type === ARGUMENT && typeof e.value === 'string' && names.has(e.value)) {
        return { ...el, type: NUMBER, style: null } as unknown as El;
      }
      if (e.type === SELECT || e.type === PLURAL) {
        const opts = (el as unknown as { options: Record<string, { value: Ast }> }).options;
        const next: Record<string, { value: Ast }> = {};
        for (const [k, o] of Object.entries(opts)) next[k] = { ...o, value: walk(o.value) };
        return { ...el, options: next } as unknown as El;
      }
      if (e.type === TAG) {
        const children = (el as unknown as { children: Ast }).children;
        return { ...el, children: walk(children) } as unknown as El;
      }
      return el;
    });
  return walk(ast);
}

const nfCache = new Map<string, Intl.NumberFormat>();
function nf(lng: string, digits?: number): Intl.NumberFormat {
  const id = `${lng}|${digits ?? ''}`;
  let f = nfCache.get(id);
  if (!f) {
    f = new Intl.NumberFormat(lng, digits == null ? undefined : { minimumFractionDigits: digits, maximumFractionDigits: digits });
    nfCache.set(id, f);
  }
  return f;
}

/** A number for display in [lng]: grouped ("10,000" / "10.000"), with
 *  exactly [digits] decimals when given. Use for counts shown outside a
 *  message (pills, tiles) so they match the counts inside messages. */
export function formatNumber(n: number, lng: string, digits?: number): string {
  return nf(lng, digits).format(n);
}

export type IcuValues = Record<string, unknown>;

const msgCache = new Map<string, IntlMessageFormat>();

/** Formats an ICU message like IntlMessageFormat, with numeric arguments
 *  grouped per locale. [key] selects the ARB exceptions above. Throws like
 *  IntlMessageFormat (missing value, malformed message). */
export function formatIcu(res: string, values: IcuValues, lng: string, key?: string): string {
  const raw = key ? RAW_NUMBER_ARGS[key] : undefined;
  const digits = key ? DECIMAL_DIGITS_ARGS[key] : undefined;
  let vals = values;
  if (digits) {
    vals = { ...values };
    for (const [name, d] of Object.entries(digits)) {
      const v = vals[name];
      if (typeof v === 'number') vals[name] = formatNumber(v, lng, d);
    }
  }
  const names = Object.keys(vals)
    .filter((n) => typeof vals[n] === 'number' && !(raw?.includes(n) ?? false))
    .sort();
  const id = `${lng}\u0000${names.join(',')}\u0000${res}`;
  let fmt = msgCache.get(id);
  if (!fmt) {
    const plain = new IntlMessageFormat(res, lng);
    fmt = names.length === 0 ? plain : new IntlMessageFormat(groupArgs(plain.getAst(), new Set(names)), lng);
    if (msgCache.size > 2000) msgCache.clear();
    msgCache.set(id, fmt);
  }
  return String(fmt.format(vals as Parameters<IntlMessageFormat['format']>[0]));
}
